import { Component, Input, OnInit } from "@angular/core";
import { MainState } from "@app/store/state";
import { ContentTypeInterface } from "@core/interfaces/content-type.interface";
import { TogglePropertyInterface } from "@core/interfaces/toggle-property.interface";
import { CommonApiService } from "@core/services/api/classic/common/common-api.service";
import { LoadingService } from "@core/services/loading.service";
import { NgbActiveModal } from "@ng-bootstrap/ng-bootstrap";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { BaseComponentDirective } from "@shared/components/base-component.directive";
import { forkJoin, Observable, of } from "rxjs";
import { catchError, take } from "rxjs/operators";

@Component({
  selector: "astrobin-equipment-follow-options-modal",
  templateUrl: "./equipment-follow-options-modal.component.html",
  styleUrls: ["./equipment-follow-options-modal.component.scss"]
})
export class EquipmentFollowOptionsModalComponent extends BaseComponentDirective implements OnInit {
  @Input() forumTagContentType: ContentTypeInterface;
  @Input() equipmentContentType: ContentTypeInterface;
  @Input() itemId: number;
  @Input() forumTagId: number;
  @Input() itemName: string;
  @Input() itemBrandName: string;
  @Input() itemType: string;
  @Input() currentUserId: number;

  followImages = false;
  followForum = false;
  loading = true;
  saving = false;
  error: string | null = null;

  private _existingImageFollowId: number | null = null;
  private _existingForumFollowId: number | null = null;

  constructor(
    public readonly store$: Store<MainState>,
    public readonly modal: NgbActiveModal,
    private readonly _commonApiService: CommonApiService,
    private readonly _loadingService: LoadingService,
    private readonly _translateService: TranslateService
  ) {
    super(store$);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this._loadCurrentFollowStates();
  }

  save(): void {
    this.saving = true;
    this.error = null;

    const operations: Observable<TogglePropertyInterface | void>[] = [];

    // Handle image follow state
    if (this.followImages && !this._existingImageFollowId) {
      // Create new image follow
      operations.push(
        this._commonApiService.createToggleProperty({
          propertyType: "follow",
          user: this.currentUserId,
          contentType: this.equipmentContentType.id,
          objectId: this.itemId
        })
      );
    } else if (!this.followImages && this._existingImageFollowId) {
      // Delete existing image follow
      operations.push(this._commonApiService.deleteToggleProperty(this._existingImageFollowId));
    }

    // Handle forum follow state
    if (this.followForum && !this._existingForumFollowId) {
      // Create new forum follow
      operations.push(
        this._commonApiService.createToggleProperty({
          propertyType: "follow",
          user: this.currentUserId,
          contentType: this.forumTagContentType.id,
          objectId: this.forumTagId
        })
      );
    } else if (!this.followForum && this._existingForumFollowId) {
      // Delete existing forum follow
      operations.push(this._commonApiService.deleteToggleProperty(this._existingForumFollowId));
    }

    if (operations.length === 0) {
      this.modal.close({ followImages: this.followImages, followForum: this.followForum });
      return;
    }

    forkJoin(operations)
      .pipe(
        take(1),
        catchError(error => {
          this.error = this._translateService.instant(
            "An error occurred while saving your preferences. Please try again."
          );
          this.saving = false;
          return of(null);
        })
      )
      .subscribe(results => {
        if (results !== null) {
          this.modal.close({ followImages: this.followImages, followForum: this.followForum });
        }
      });
  }

  cancel(): void {
    this.modal.dismiss();
  }

  getDisplayName(): string {
    const brand = this.itemBrandName || this._translateService.instant("DIY");
    return `${brand} ${this.itemName}`;
  }

  private _loadCurrentFollowStates(): void {
    const imageFollow$ = this._commonApiService.getToggleProperty({
      propertyType: "follow",
      user: this.currentUserId,
      contentType: this.equipmentContentType.id,
      objectId: this.itemId
    });

    const forumFollow$ = this._commonApiService.getToggleProperty({
      propertyType: "follow",
      user: this.currentUserId,
      contentType: this.forumTagContentType.id,
      objectId: this.forumTagId
    });

    forkJoin([imageFollow$, forumFollow$])
      .pipe(
        take(1),
        catchError(error => {
          this.loading = false;
          this.error = this._translateService.instant("Failed to load current follow states.");
          return of([null, null]);
        })
      )
      .subscribe(([imageFollow, forumFollow]) => {
        if (imageFollow) {
          this.followImages = true;
          this._existingImageFollowId = imageFollow.id;
        }
        if (forumFollow) {
          this.followForum = true;
          this._existingForumFollowId = forumFollow.id;
        }
        this.loading = false;
      });
  }
}
